import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { IntlContextProvider } from '../../contexts/intl-context';
import { SupportedLocale } from '../../i18n';
import {
  isSupportedLocale,
  localizePath,
  resolveOfflineLocale,
  setStoredLocale,
  stripLocale,
} from '../../i18n/locale-path';
import GeneratedFeature from '../redirection/GeneratedFeature';

/** Layout for /:locale/* — validates locale and mounts IntlProvider.
 *  Non-locale single segments (QR codes) render GeneratedFeature. */
export function LocaleLayout() {
  const { locale: localeParam } = useParams<{ locale: string }>();
  const navigate = useNavigate();

  const handleLocaleChange = useCallback(
    (next: SupportedLocale) => {
      setStoredLocale(next);
      const rest = stripLocale(window.location.pathname);
      navigate(`${localizePath(next, rest)}${window.location.search}${window.location.hash}`, {
        replace: true,
      });
    },
    [navigate]
  );

  useEffect(() => {
    if (isSupportedLocale(localeParam)) {
      setStoredLocale(localeParam);
    }
  }, [localeParam]);

  if (!isSupportedLocale(localeParam)) {
    return (
      <BareCodeIntlLayout>
        <GeneratedFeature code={localeParam} />
      </BareCodeIntlLayout>
    );
  }

  return (
    <IntlContextProvider locale={localeParam} onLocaleChange={handleLocaleChange}>
      <Outlet />
    </IntlContextProvider>
  );
}

/** Layout for bare QR routes — locale from storage/browser */
export function BareCodeIntlLayout({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => resolveOfflineLocale());

  const handleLocaleChange = useCallback((next: SupportedLocale) => {
    setStoredLocale(next);
    setLocaleState(next);
  }, []);

  return (
    <IntlContextProvider locale={locale} onLocaleChange={handleLocaleChange}>
      {children}
    </IntlContextProvider>
  );
}
